import { useEffect, useId, useMemo, useRef, useState, type RefObject } from 'react';
import type { ConversationTurn } from '../../lib/conversationPresentation';
import { i18nService } from '../../services/i18n';

/** 长对话的提问导航；直接复用已展示轮次，不另发请求或创建第二套会话状态。 */
export default function CoworkHistoryNavigator({ turns, scrollContainerRef, onLocate }: {
  /** 与正文相同的已分组轮次；孤立系统消息与子代理内部消息不生成提问锚点。 */
  turns: ConversationTurn[];
  scrollContainerRef: RefObject<HTMLDivElement>;
  /** 调用会话统一定位入口，同时取消自动跟随并展开目标轮次。 */
  onLocate: (turnId: string, messageId: string) => void;
}) {
  const anchors = useMemo(() => turns.flatMap(turn => turn.userMessage && !turn.userMessage.metadata?.parentToolUseId && !turn.userMessage.metadata?.isThinking
    ? [{ id: turn.id, messageId: turn.userMessage.id, preview: turn.userMessage.content.replace(/\s+/g, ' ').trim().slice(0, 240) }] : []), [turns]);
  const anchorKey = JSON.stringify(anchors.map(anchor => anchor.id));
  const [activeId, setActiveId] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const markerListRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  useEffect(() => {
    const container = scrollContainerRef.current;
    const ids = new Set<string>(JSON.parse(anchorKey));
    if (!container || ids.size < 5) return;
    let frame = 0;
    /** 动画帧合并滚动与内容变化；比较视口中的真实位置，兼容折叠工具和流式增高。 */
    const measure = () => {
      frame = 0;
      const line = container.getBoundingClientRect().top + Math.min(120, container.clientHeight * 0.35);
      const nodes = [...container.querySelectorAll<HTMLElement>('[data-turn-id]')].filter(node => ids.has(node.dataset.turnId || ''));
      let current = nodes[0]?.dataset.turnId || '';
      for (const node of nodes) {
        if (node.getBoundingClientRect().top > line) break;
        current = node.dataset.turnId || current;
      }
      // 最后一轮不足一屏时无法滚到激活线；到达底部仍应正确标记最后一个问题。
      if (nodes.length && container.scrollHeight - container.clientHeight - container.scrollTop <= 2) current = nodes[nodes.length - 1].dataset.turnId || current;
      setActiveId(current);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    const resize = new ResizeObserver(schedule);
    resize.observe(container);
    if (container.firstElementChild) resize.observe(container.firstElementChild);
    const mutation = new MutationObserver(schedule);
    mutation.observe(container, { childList: true, subtree: true });
    container.addEventListener('scroll', schedule, { passive: true });
    schedule();
    return () => { cancelAnimationFrame(frame); resize.disconnect(); mutation.disconnect(); container.removeEventListener('scroll', schedule); };
  }, [anchorKey, scrollContainerRef]);

  useEffect(() => {
    const list = markerListRef.current;
    const marker = [...(list?.querySelectorAll<HTMLButtonElement>('button') ?? [])].find(button => button.dataset.anchorId === activeId);
    if (!list || !marker || list.contains(document.activeElement)) return;
    // 仅滚动导航条自身，避免 scrollIntoView 连带改变正文或整页的位置。
    if (marker.offsetTop < list.scrollTop) list.scrollTop = marker.offsetTop;
    else if (marker.offsetTop + marker.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = marker.offsetTop + marker.offsetHeight - list.clientHeight;
  }, [activeId]);

  if (anchors.length < 5) return null;
  const preview = anchors.find(anchor => anchor.id === previewId);
  return <nav aria-label={i18nService.t('conversationHistory')} className="group absolute left-1 top-1/2 z-20 hidden -translate-y-1/2 md:block"
    onMouseLeave={() => setPreviewId(null)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setPreviewId(null); }}>
    <div ref={markerListRef} className="relative max-h-[280px] w-7 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {anchors.map((anchor, index) => <button key={anchor.id} type="button" data-anchor-id={anchor.id}
        tabIndex={activeId === anchor.id || (!activeId && index === 0) ? 0 : -1}
        aria-label={`${index + 1}. ${anchor.preview || '#' + (index + 1)}`} aria-current={activeId === anchor.id ? 'location' : undefined}
        aria-describedby={previewId === anchor.id ? tooltipId : undefined}
        onMouseEnter={() => setPreviewId(anchor.id)} onFocus={() => setPreviewId(anchor.id)}
        onClick={() => onLocate(anchor.id, anchor.messageId)}
        onKeyDown={event => {
          const delta = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
          if (!delta && event.key !== 'Home' && event.key !== 'End' && event.key !== 'Escape') return;
          event.preventDefault();
          if (event.key === 'Escape') { setPreviewId(null); return; }
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? anchors.length - 1 : Math.max(0, Math.min(anchors.length - 1, index + delta));
          const button = markerListRef.current?.querySelectorAll('button')[next];
          button?.focus({ preventScroll: true });
          if (button && markerListRef.current) markerListRef.current.scrollTop = button.offsetTop - 4 * button.offsetHeight;
        }}
        className="flex h-7 w-7 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-claude-accent">
        <span className={`h-0.5 rounded-full transition-[width,background-color,opacity] duration-[190ms] ease-out motion-reduce:transition-none group-hover:w-4 group-focus-within:w-4 ${activeId === anchor.id ? 'w-4 bg-claude-accent opacity-100' : 'w-2 bg-claude-textSecondary/40 dark:bg-claude-darkTextSecondary/50'}`} />
      </button>)}
    </div>
    {preview && <div id={tooltipId} role="tooltip" className="pointer-events-none absolute left-9 top-1/2 w-max max-w-[min(260px,calc(100vw-60px))] -translate-y-1/2 rounded-xl border border-claude-border dark:border-claude-darkBorder bg-claude-bg dark:bg-claude-darkSurface px-3 py-2 text-xs text-claude-text dark:text-claude-darkText shadow-lg"><span className="line-clamp-3 break-words">{preview.preview || `#${anchors.indexOf(preview) + 1}`}</span></div>}
  </nav>;
}
